import { defineStore } from 'pinia'
import { api } from '@/app/services/api.service'
import type { AccountsState } from './types/accounts.types'

export const useAccountsStore = defineStore('accounts', {
  state: (): AccountsState => ({
    officer_accounts: [],
    user_accounts: [],
    business_accounts: [],
    individual_accounts_count: 0,
    partner_accounts_count: 0,
    mda_accounts_count: 0,
    private_organisations_accounts_count: 0,
    business_accounts_count: 0,
    officer_accounts_count: 0,
    user_details: {},
    user_error: null,
    reactivationSuccess: null,
    reactivationError: null,
    idAvailability: null,
    ninInfo: null,
    ninError: null,
    idAttachment: null,
    idAttachmentError: null,
    signatureUpdate: null,
    signatureUpdateError: null,
    signatureDetails: null,
    signatureDetailsError: null,
    clients: null,
  }),

  getters: {
    getOfficerAccounts: (state) => state.officer_accounts,
    getUserAccounts: (state) => state.user_accounts,
    getBusinessAccounts: (state) => state.business_accounts,
    getUserDetails: (state) => state.user_details,
    getReactivationSuccess: (state) => state.reactivationSuccess,
    getReactivationError: (state) => state.reactivationError,
    getIndividualAccountsCount: (state) => state.individual_accounts_count,
    getPartnerAccountsCount: (state) => state.partner_accounts_count,
    getMdaAccountsCount: (state) => state.mda_accounts_count,
    getPrivateOrganisationsAccountsCount: (state) => state.private_organisations_accounts_count,
    getBusinessAccountsCount: (state) => state.business_accounts_count,
    getStaffAccountsCount: (state) => state.officer_accounts_count,
    getIdAvailability: (state) => state.idAvailability,
    getNinInfo: (state) => state.ninInfo,
    getNinError: (state) => state.ninError,
    getIdAttachment: (state) => state.idAttachment,
    getIdAttachmentError: (state) => state.idAttachmentError,
    getSignatureUpdateSuccess: (state) => state.signatureUpdate,
    getSignatureUpdateError: (state) => state.signatureUpdateError,
    getSignatureDetails: (state) => state.signatureDetails,
    getSignatureDetailsError: (state) => state.signatureDetailsError,
    getUserError: (state) => state.user_error,
    getClients: (state) => state.clients,
  },

  actions: {
    async fetchUsers(queryString: string) {
      try {
        const response = await api().get('auth/api/users?' + queryString)
        this.officer_accounts = response.data
      } catch (error: any) {
        this.user_error = error.response?.data
      }
    },

    async fetchStaffAccounts(queryString: string) {
      try {
        const response = await api().get('auth/api/users/staff?' + queryString)
        this.officer_accounts = response.data
      } catch (error: any) {
        this.user_error = error.response?.data
      }
    },

    async fetchBusinessAccounts(queryString: string) {
      try {
        const response = await api().get('auth/api/users/business?' + queryString)
        this.business_accounts = response.data
      } catch (error: any) {
        this.user_error = error.response?.data
      }
    },

    async fetchClientAccounts(queryString: string) {
      try {
        const response = await api().get('auth/api/users/individual?' + queryString)
        this.user_accounts = response.data
      } catch (error: any) {
        this.user_error = error.response?.data
      }
    },

    async fetchPartners(type: string) {
      try {
        const response = await api().get('auth/api/partners?partner-type=' + type)
        this.user_accounts = response.data
      } catch (error: any) {
        this.user_error = error.response?.data
      }
    },

    async fetchUserDetails(userId: string | number) {
      try {
        const response = await api().get('auth/api/users/' + userId)
        this.user_details = response.data
      } catch (error: any) {
        this.user_error = error.response?.data
      }
    },

    async doResendActivationLink(data: any) {
      try {
        const response = await api().post('/auth/api/auth/send-reactivation-link', data)
        this.reactivationSuccess = response.data
      } catch (error: any) {
        this.reactivationError = error.response?.data
      }
    },

    async fetchTotalAccountMetricsByType(type: string) {
      try {
        const response = await api().get('/auth/api/users/user-account-type/' + type)
        if (type === 'BACKOFFICE') {
          this.officer_accounts_count = response.data
        } else if (type === 'PARTNER') {
          this.partner_accounts_count = response.data
        } else if (type === 'INDIVIDUAL') {
          this.individual_accounts_count = response.data
        } else if (type === 'MDA') {
          this.mda_accounts_count = response.data
        } else if (type === 'BUSINESS') {
          this.business_accounts_count = response.data
        } else if (type === 'PRIVATE_ORGANIZATION') {
          this.private_organisations_accounts_count = response.data
        }
      } catch (error: any) {
        this.user_error = error.response?.data
      }
    },

    async changeAccountStatus(data: any) {
      try {
        const response = await api().post('/auth/api/users/update-account-status', data)
        this.reactivationSuccess = response.data
      } catch (error: any) {
        this.reactivationError = error.response?.data
      }
    },

    async validateNin(data: any) {
      try {
        const response = await api('https://nira.ursbonline.go.ug').post('/api/nira', data)
        this.ninInfo = response.data
      } catch (error: any) {
        this.ninError = error.response?.data
      }
    },

    async checkIdAvailability(data: any) {
      try {
        const response = await api().post('/auth/api/users/checkIdNumberAvailability', data)
        this.idAvailability = response.data
      } catch (error: any) {
        this.user_error = error.response?.data
      }
    },

    async updateStaffDepartment(data: any) {
      try {
        const response = await api().post('/auth/api/users/update-staff-department', data)
        this.reactivationSuccess = response.data
      } catch (error: any) {
        this.reactivationError = error.response?.data
      }
    },

    async updateStaffContacts(data: any) {
      try {
        const response = await api().post('/auth/api/users/update-user', data)
        this.reactivationSuccess = response.data
      } catch (error: any) {
        this.reactivationError = error.response?.data
      }
    },

    async updateAccountContactDetails(data: any) {
      try {
        const response = await api().post('/auth/api/partner/update-contact-person', data)
        this.reactivationSuccess = response.data
      } catch (error: any) {
        this.reactivationError = error.response?.data
      }
    },

    async fetchIdAttachment(userId: string | number) {
      try {
        const response = await api().get('auth/api/users/user-attachment/' + userId)
        this.idAttachment = response.data
      } catch (error: any) {
        this.idAttachmentError = error.response?.data
      }
    },

    async doUpdateStaffSignature(data: any) {
      try {
        const response = await api().post('/auth/api/users/staff-signature/update', data)
        this.signatureUpdate = response.data
      } catch (error: any) {
        this.signatureUpdateError = error.response?.data
      }
    },

    async fetchStaffSignature(data: { id: string | number }) {
      try {
        const response = await api().get('/auth/api/users/staff-signature/' + data.id)
        this.signatureDetails = response.data
      } catch (error: any) {
        this.signatureDetailsError = error.response?.data
      }
    },

    async searchClients(queryParams: string) {
      try {
        const response = await api().get('auth/api/users/individual?' + queryParams)
        this.clients = response.data
      } catch (error: any) {
        this.user_error = error.response?.data
      }
    },
  },
})
